import { useState, useCallback, useEffect } from 'react';
import {
  depositService,
  DepositOptions,
  DepositResult,
} from '../services/depositService';

export interface UseDepositReturn {
  // State
  isLoading: boolean;
  error: string | null;

  // Balances (SWM only)
  swmBalance: string;
  poolBalance: string;

  // Allowance (SWM only)
  swmAllowance: string;

  // Actions (SWM only)
  deposit: (options: DepositOptions) => Promise<DepositResult>;
  approveTokens: (tokenType: 'SWM', amount: string) => Promise<DepositResult>;
  refreshBalances: () => Promise<void>;
  checkBalance: (tokenType: 'SWM', amount: string) => Promise<boolean>;
  checkAllowance: (tokenType: 'SWM', amount: string) => Promise<boolean>;
}

export const useDeposit = (userAddress?: string): UseDepositReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Balances (SWM only)
  const [swmBalance, setSwmBalance] = useState('0');
  const [poolBalance, setPoolBalance] = useState('0');

  // Allowance (SWM only)
  const [swmAllowance, setSwmAllowance] = useState('0');

  // Refresh all balances and allowances
  const refreshBalances = useCallback(async () => {
    if (!userAddress) return;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch SWM wallet balance
      const swm = await depositService.getBalance('SWM', userAddress);
      setSwmBalance(swm);
      // Fetch pool balance (credits deposited)
      const poolBal = await depositService.getUserBalance(userAddress);
      setPoolBalance(poolBal);

      // Fetch SWM allowance
      const swmAllow = await depositService.getAllowance('SWM', userAddress);
      setSwmAllowance(swmAllow);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to refresh balances';
      setError(errorMessage);
      console.error('Error refreshing balances:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userAddress]);

  // Deposit function
  const deposit = useCallback(
    async (options: DepositOptions): Promise<DepositResult> => {
      try {
        setIsLoading(true);
        setError(null);

        // Proceed with deposit even if reads are flaky; internal service handles retries/fallbacks
        const result = await depositService.deposit(options);

        if (result.success) {
          // Refresh balances after successful deposit
          await refreshBalances();
        } else {
          setError(result.userGuidance || result.error || 'Deposit failed');
        }

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Deposit failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsLoading(false);
      }
    },
    [refreshBalances]
  );

  // Approve tokens function
  const approveTokens = useCallback(
    async (tokenType: 'SWM', amount: string): Promise<DepositResult> => {
      try {
        setIsLoading(true);
        setError(null);

        // For SWM, service will try permit-first; for others, go straight to approve
        const result = await depositService.approveTokens(tokenType, amount);

        if (result.success) {
          // Refresh allowances after successful approval
          await refreshBalances();
        } else {
          setError(result.userGuidance || result.error || 'Approval failed');
        }

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Approval failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsLoading(false);
      }
    },
    [refreshBalances]
  );

  // Check balance function
  const checkBalance = useCallback(
    async (tokenType: 'SWM', amount: string): Promise<boolean> => {
      if (!userAddress) return false;

      try {
        return await depositService.checkBalance(
          tokenType,
          userAddress,
          amount
        );
      } catch (err) {
        console.error('Error checking balance:', err);
        return false;
      }
    },
    [userAddress]
  );

  // Check allowance function
  const checkAllowance = useCallback(
    async (tokenType: 'SWM', amount: string): Promise<boolean> => {
      if (!userAddress) return false;

      try {
        return await depositService.checkAllowance(
          tokenType,
          userAddress,
          amount
        );
      } catch (err) {
        console.error('Error checking allowance:', err);
        return false;
      }
    },
    [userAddress]
  );

  // Load balances on mount and when userAddress changes
  useEffect(() => {
    if (userAddress) {
      refreshBalances();
    }
  }, [userAddress, refreshBalances]);

  // Avoid live event filters on unstable RPC (circuit breaker); rely on explicit refreshes instead
  // If needed later, switch to polling via depositService.watchDepositHistory

  return {
    // State
    isLoading,
    error,

    // Balances
    swmBalance,
    poolBalance,

    // Allowance
    swmAllowance,

    // Actions
    deposit,
    approveTokens,
    refreshBalances,
    checkBalance,
    checkAllowance,
  };
};
